import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import api, { peso, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const Row = ({ label, value, bold, negative }) => (
  <div className={`flex justify-between py-1.5 ${bold ? "border-t border-slate-200 mt-1 pt-2 font-semibold" : ""}`}>
    <span className={bold ? "text-slate-900" : "text-slate-600"}>{label}</span>
    <span className={`${negative ? "text-red-600" : "text-slate-800"} ${bold ? "text-blue-700" : ""} font-mono text-sm`}>{value}</span>
  </div>
);

export default function Payroll() {
  const [records, setRecords] = useState([]);
  const [slip, setSlip] = useState(null);

  useEffect(() => { api.get("/payroll/records").then(({ data }) => setRecords(data)); }, []);

  const openSlip = async (id) => {
    const { data } = await api.get(`/payroll/records/${id}`);
    setSlip(data);
  };

  const downloadSlip = () => {
    if (!slip) return;
    const r = slip.record, e = slip.employee;
    const text = `PAYSLIP — ${r.period_label}\n` +
      `Employee: ${e?.first_name} ${e?.last_name} (${e?.employee_no})\n` +
      `Position: ${e?.position}\n\n` +
      `Basic Salary: ${peso(r.basic_salary)}\nAllowances: ${peso(r.allowances)}\nOvertime: ${peso(r.overtime)}\n` +
      `Gross Pay: ${peso(r.gross_pay)}\n\nTax: ${peso(r.tax)}\nSSS: ${peso(r.sss)}\nPhilHealth: ${peso(r.philhealth)}\nPag-IBIG: ${peso(r.pagibig)}\n` +
      `Total Deductions: ${peso(r.total_deductions)}\n\nNET PAY: ${peso(r.net_pay)}\n`;
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `payslip-${r.period}.txt`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="uemp-fade-in">
      <PageHeader title="Payroll Records" subtitle="View your payslips and compensation history" />
      <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-2.5 text-sm mb-5 flex items-center gap-2">
        <Icons.Lock className="h-4 w-4" /> Payroll records are read-only. Contact HR/Payroll for adjustments.
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Period</TableHead>
              <TableHead className="hidden md:table-cell">Gross Pay</TableHead>
              <TableHead className="hidden md:table-cell">Deductions</TableHead>
              <TableHead>Net Pay</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Payslip</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="payroll-table">
            {records.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">No payroll records.</TableCell></TableRow>}
            {records.map((r) => (
              <TableRow key={r.id} data-testid={`payroll-row-${r.period}`}>
                <TableCell className="font-medium text-slate-800">{r.period_label}</TableCell>
                <TableCell className="hidden md:table-cell font-mono text-sm text-slate-600">{peso(r.gross_pay)}</TableCell>
                <TableCell className="hidden md:table-cell font-mono text-sm text-red-600">{peso(r.total_deductions)}</TableCell>
                <TableCell className="font-mono text-sm font-semibold text-slate-900">{peso(r.net_pay)}</TableCell>
                <TableCell><Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 text-[10px]">{r.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => openSlip(r.id)} data-testid={`view-payslip-${r.period}`}>
                    <Icons.Eye className="h-3.5 w-3.5 mr-1" /> View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!slip} onOpenChange={() => setSlip(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Payslip — {slip?.record.period_label}</DialogTitle></DialogHeader>
          {slip && (
            <div data-testid="payslip-detail">
              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <div className="font-semibold text-slate-900">{slip.employee?.first_name} {slip.employee?.last_name}</div>
                <div className="text-sm text-slate-500">{slip.employee?.position} · {slip.employee?.employee_no}</div>
                <div className="text-xs text-slate-400 mt-1">Released {fmtDate(slip.record.release_date)}</div>
              </div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Earnings</div>
              <Row label="Basic Salary" value={peso(slip.record.basic_salary)} />
              <Row label="Allowances" value={peso(slip.record.allowances)} />
              <Row label="Overtime" value={peso(slip.record.overtime)} />
              <Row label="Gross Pay" value={peso(slip.record.gross_pay)} bold />
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 mt-4">Deductions</div>
              <Row label="Withholding Tax" value={peso(slip.record.tax)} negative />
              <Row label="SSS" value={peso(slip.record.sss)} negative />
              <Row label="PhilHealth" value={peso(slip.record.philhealth)} negative />
              <Row label="Pag-IBIG" value={peso(slip.record.pagibig)} negative />
              <Row label="Total Deductions" value={peso(slip.record.total_deductions)} bold negative />
              <div className="bg-blue-600 text-white rounded-lg p-4 mt-4 flex justify-between items-center">
                <span className="font-medium">NET PAY</span>
                <span className="text-xl font-semibold font-mono">{peso(slip.record.net_pay)}</span>
              </div>
              <Button onClick={downloadSlip} className="w-full mt-4 bg-slate-900 hover:bg-slate-800" data-testid="download-payslip">
                <Icons.Download className="h-4 w-4 mr-1.5" /> Download Payslip
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
